import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import TypesInput from "@features/types-input/types-input";
import { MEDIA_TYPES } from "@constants";
import { Box, Button, Grid, IconButton, Stack, Tooltip } from "@mzc-pdc/ui";
import Loading from "@components/loader/Loading";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconAdd, IconCancel, IconRefresh, ImageEmptyItem } from "@mzc-cloudplex/icons";
import AutocompleteTableList from "@features/autocomplete-table/autocomplete-table-list";
import AssetColumns from "./panel-table-columns-assets";
import { clearPropertyPanelLinkedAssets, getLinkedAssets, updateCollectionAction } from "@modules/propertyPanel";
import { autocompleteTableHoverStyle } from "@features/autocomplete-table/autocomplete-table-list.styled";
import * as propertyPanelActions from "@modules/propertyPanel";
import DialogAddLinkedAssetsV2 from "@routes/asset/ui/detail/dialog-add-linked-assets-v2";
import { updateCollection } from "@modules/collection";
import { updateCollectionData } from "@modules/collections";
import { Popover } from "@mui/material";
import useDialog from "@hooks/useDialog";
import SelectFolderPath from "@components/modals/SelectFolderPath";
import DialogDelete from "@features/dialog/dialog-delete";

//panel type
// normal: edit 안할 시 일반 Typography + edit시 input
// textarea: edit 안할 시 일반 Typography + edit시 textarea
// date: edit안할 시 일반 Typography와 유저 조회 버튼 + edit 불가
// tags: 태그 전용 컴포넌트
// folder: 폴더 전용 컴포넌트
// listView: edit안할 시 컬렉션 목록 + edit시 collection autocomplete

const PanelAssetsView = ({ isEditButtonDisabled }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { dialog, openDialog, closeDialog } = useDialog();

    const propertyPanel = useSelector(({ propertyPanel }) => propertyPanel);

    const [pending, setPending] = useState(false);
    const [dialogAddLinkedAssetsActive, setDialogAddLinkedAssetsActive] = useState(false);
    const [actionPending, setActionPending] = useState(false);
    const [filterAssetType, setFilterAssetType] = useState([]);
    const [showCreatePopover, setShowCreatePopover] = useState(null);
    const [actionTarget, setActionTarget] = useState(null);

    const getCollectionLinkedAssets = async (refresh = false) => {
        if (
            (!refresh &&
                propertyPanel.data?.linkedAssets?.results?.length > 0 &&
                !propertyPanel.data?.linkedAssets?.nextToken) ||
            actionPending
        )
            return;
        setPending(true);
        const data = await dispatch(
            getLinkedAssets({ id: propertyPanel.targetId, nextToken: propertyPanel.data?.linkedAssets?.nextToken }),
        );
        setPending(false);
        return data;
    };

    useEffect(() => {
        if (!propertyPanel.targetId) return;
        getCollectionLinkedAssets();
    }, [propertyPanel.targetId]);

    const onFetchLinkedCollections = () => {
        if (!propertyPanel?.data?.nextToken || propertyPanel.pending) return;
        const params = { nextToken: propertyPanel.data?.nextToken };
        dispatch(getLinkedAssets({ id: propertyPanel.targetId, params }));
    };

    const onHandleClickRefresh = async () => {
        await dispatch(clearPropertyPanelLinkedAssets());
        const data = await getCollectionLinkedAssets(true);
        if (data) {
            dispatch(
                updateCollectionData({
                    id: propertyPanel.targetId,
                    type: "assetTotalCount",
                    value: data.totalCount,
                }),
            );
        }
    };

    const onHandleClickDeleteLinkedAsset = useCallback(
        async (id) => {
            try {
                if (actionPending) return;
                setActionPending(true);

                await dispatch(
                    updateCollectionAction({
                        contentId: propertyPanel.targetId,
                        params: {
                            action: "REMOVE_ITEMS",
                            data: {
                                assetIds: [id],
                            },
                        },
                    }),
                );

                onHandleClickRefresh(true);
                closeDialog();
            } catch (error) {
                console.error(error);
            } finally {
                setActionPending(null);
            }
        },
        [propertyPanel],
    );

    const onHandleClickAddAssets = async (data, shouldUpdatePrimaryAsset) => {
        try {
            await dispatch(
                updateCollectionAction({
                    contentId: propertyPanel.targetId,
                    params: {
                        action: "ADD_ITEMS",
                        data,
                    },
                }),
            );
            if (shouldUpdatePrimaryAsset) {
                await dispatch(
                    updateCollection({
                        id: propertyPanel.targetId,
                        params: { primaryAsset: { id: data.assetIds[0] } },
                        ignoreAction: true,
                    }),
                );
            }
            onHandleClickRefresh();
            closeDialog();
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickAddFolder = async ({ folderId, includeSubFolders }) => {
        try {
            await onHandleClickAddAssets({
                virtualFolder: {
                    id: folderId,
                    recursive: includeSubFolders,
                },
            });
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickAddLinkedAssets = async (selectedAssets) => {
        try {
            const shouldUpdatePrimaryAsset = !propertyPanel.data.primaryAsset && selectedAssets.length;

            await onHandleClickAddAssets(
                {
                    assetIds: selectedAssets.map((asset) => asset.id),
                },
                shouldUpdatePrimaryAsset,
            );
        } catch (error) {
            console.error(error);
        }
    };

    const filteredLinkedAssets = useMemo(() => {
        if (!propertyPanel.data?.linkedAssets?.results?.length) return [];

        if (!filterAssetType.length) return propertyPanel.data?.linkedAssets?.results;
        return propertyPanel.data?.linkedAssets?.results?.filter((asset) =>
            filterAssetType.find(({ value }) => value === asset.mediaType),
        );
    }, [propertyPanel.data.linkedAssets, filterAssetType]);

    return (
        <>
            {dialog === "addAssets" && (
                <DialogAddLinkedAssetsV2
                    onSubmit={onHandleClickAddLinkedAssets}
                    excludeItemIds={propertyPanel.data?.linkedAssets?.results?.map((asset) => asset.id) ?? []}
                    onClose={closeDialog}
                />
            )}
            {dialog === "addFolder" && (
                <SelectFolderPath
                    open
                    domain={"Asset"}
                    submitText={`Select`}
                    width={"600px"}
                    title={"Add assets from folder"}
                    subFoldersOption={true}
                    useNewFolder={false}
                    onSubmit={onHandleClickAddFolder}
                    onClose={closeDialog}
                />
            )}
            {dialog === "delete" && (
                <>
                    <DialogDelete
                        items={actionTarget}
                        title={t(`common::label::Remove the item?`)}
                        contents={
                            <Grid maxWidth={"432px"}>
                                <Typography variant={"body1_400"} color={theme.palette.secondary[800]}>
                                    <Grid item>
                                        <span>
                                            <Trans
                                                i18nKey={`common::msg::“{{name}}” will be removed.`}
                                                values={{
                                                    name: actionTarget.name,
                                                }}
                                            />
                                        </span>
                                    </Grid>
                                </Typography>
                            </Grid>
                        }
                        actionButtonTitle={"Remove"}
                        actionPending={actionPending}
                        onClickClose={() => closeDialog()}
                        onClickSubmit={() => {
                            onHandleClickDeleteLinkedAsset(actionTarget.id);
                        }}
                    />
                </>
            )}
            <Box sx={{ padding: "12px 16px" }}>
                <Grid container flexDirection={"column"}>
                    <TypesInput
                        types={filterAssetType.map((type) => ({ value: type.value, label: type.value }))}
                        placeholder={t(`common::label::All Media Types`)}
                        options={Object.values(MEDIA_TYPES)
                            .filter((type) => !["CAPTION", "PEOPLE"].includes(type))
                            .map((type) => ({
                                title: type,
                                id: type,
                                label: type,
                            }))}
                        onAddType={(ext) => {
                            setFilterAssetType([...filterAssetType, ext]);
                        }}
                        onDeleteType={(index) => {
                            setFilterAssetType(filterAssetType.filter((t, idx) => idx !== index));
                        }}
                    />
                    <Grid container justifyContent={"center"}>
                        <Grid
                            container
                            flexDirection={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            sx={{ mt: 1 }}
                        >
                            <Grid item>
                                <Typography
                                    variant={"body1_400"}
                                    component={"span"}
                                    color={theme.palette.secondary[800]}
                                >
                                    Total
                                </Typography>
                                <Typography
                                    variant={"body1_400"}
                                    component={"span"}
                                    color={theme.palette.primary[400]}
                                    sx={{ ml: 0.5 }}
                                >
                                    {propertyPanel.data?.assetTotalCount ?? 0}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Tooltip title={t(`common::label::Refresh`)} sx={{ mr: 1.5 }}>
                                    <IconButton circled size={"small"} onClick={onHandleClickRefresh}>
                                        <IconRefresh size={16} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t(`common::label::Add Assets`)}>
                                    <IconButton
                                        size={"medium"}
                                        variant={"contained"}
                                        color={"primary"}
                                        onClick={(e) => {
                                            setShowCreatePopover(e.currentTarget);
                                        }}
                                        disabled={isEditButtonDisabled}
                                    >
                                        <IconAdd size={16} />
                                    </IconButton>
                                </Tooltip>
                                <Popover
                                    open={Boolean(showCreatePopover)}
                                    anchorEl={showCreatePopover}
                                    onClose={() => setShowCreatePopover(null)}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: -4,
                                        horizontal: "right",
                                    }}
                                >
                                    <Stack
                                        className={"linked-asset-create-button-group"}
                                        direction={"column"}
                                        sx={{
                                            border: `1px solid ${theme.palette.greySecondary[100]}`,
                                            borderRadius: theme.spacing(0.5),
                                            py: theme.spacing(1),
                                            "& .MuiButton-root": {
                                                width: "200px",
                                                height: "40px",
                                                px: theme.spacing(1.75),
                                                justifyContent: "flex-start",
                                            },
                                        }}
                                        onClick={(e) => console.log(e)}
                                    >
                                        <Button
                                            color={"secondary"}
                                            variant="text"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDialog("addAssets");
                                                setShowCreatePopover(null);
                                            }}
                                        >
                                            {t(`common::label::Asset`, `Asset`)}
                                        </Button>
                                        <Button
                                            color={"secondary"}
                                            variant="text"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDialog("addFolder");
                                                setShowCreatePopover(null);
                                            }}
                                        >
                                            {t(`common::label::Folder`, `Folder`)}
                                        </Button>
                                    </Stack>
                                </Popover>
                            </Grid>
                        </Grid>
                        {pending ? (
                            <Loading></Loading>
                        ) : (
                            <Grid item sx={{ width: "100%", mt: 0.5 }}>
                                {filteredLinkedAssets.length > 0 ? (
                                    <AutocompleteTableList
                                        type={"ASSET"}
                                        data={[{ value: filteredLinkedAssets }]}
                                        columns={AssetColumns}
                                        onFetchData={getCollectionLinkedAssets}
                                        getMenuActions={(item) => (
                                            <Tooltip title={t(`common::label::Delete`)}>
                                                <IconButton
                                                    size="small"
                                                    circled
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDialog("delete");
                                                        setActionTarget(item);
                                                    }}
                                                    disabled={isEditButtonDisabled}
                                                >
                                                    <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        listStyle={autocompleteTableHoverStyle()}
                                    />
                                ) : (
                                    <Grid
                                        container
                                        sx={{ width: "100%", mt: 15 }}
                                        flexDirection={"column"}
                                        justifyContent={"center"}
                                    >
                                        <Grid container justifyContent={"center"} mb={2.5}>
                                            <ImageEmptyItem width={76} height={70} />
                                        </Grid>

                                        <Grid container justifyContent={"center"} flexDirection={"column"}>
                                            <Typography
                                                variant={"body1_500"}
                                                component={"span"}
                                                mb={0.5}
                                                color={theme.palette.secondary[800]}
                                                sx={{ textAlign: "center" }}
                                            >
                                                {t(`common::msg::No assets yet.`)}
                                            </Typography>
                                            <Typography
                                                variant={"body1_500"}
                                                component={"span"}
                                                color={theme.palette.secondary[600]}
                                                sx={{ textAlign: "center" }}
                                            >
                                                {t(`common::msg::Add it using the “+” button.`)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default PanelAssetsView;
